import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import React, { RefObject, useCallback, useContext, useMemo, useRef, useState, useImperativeHandle, forwardRef } from 'react'
import CruiseLinesSearchList from './CruiseLineSelectContentList'
import FormattedCruiseLineSearchSummary from './FormattedCruiseLineSearchSummary'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import useToggle from 'hooks/useToggle'
import * as Analytics from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'

export interface CruiseLineSelectDesktopRef {
  focus: () => void
}

interface Props {
  cruiseLineNames: Array<string>
  dropdownAnchorRef: RefObject<HTMLElement | null>
  onChange: (values: App.CruiseGlobalFilters) => void
  handlerSearchTerm: (e: React.ChangeEvent<HTMLInputElement>) => void
  handlerClearSearchTerm: () => void
  searchTerm: string
}

const CruiseLineSelectDesktop = forwardRef<CruiseLineSelectDesktopRef, Props>((props, ref) => {
  const {
    cruiseLineNames,
    onChange,
    dropdownAnchorRef,
    handlerSearchTerm,
    handlerClearSearchTerm,
    searchTerm,
  } = props
  const inputTriggerRef = useRef<HTMLInputElement>(null)
  const { value: isOpen, on: openDropdown, off: closeDropdown } = useToggle(false)
  const [showAll, setShowAll] = useState(false)
  const { cruiseLines } = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const globalSearchState = useContext(GlobalSearchStateContext)

  const onApply = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_applied',
      action: 'applied',
      category: 'cruise_line_filter',
      type: 'interaction',
    })

    Analytics.trackEvent(searchEventWithContext({
      searchEvent: { type: 'filters' },
      sortContext: { value: 'recommended' },
      searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
      filterContexts: cruiseLines.map(cruiseLine => ({
        label: 'Cruise Line',
        type: 'cruise_line',
        value: cruiseLine,
      })),
    }))

    onChange({ cruiseLines })
    closeDropdown()
  }, [onChange, cruiseLines, closeDropdown, globalSearchState])

  const onClear = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_cleared',
      action: 'cleared',
      category: 'cruise_line_filter',
      type: 'interaction',
    })
    searchDispatch({ type: GlobalSearchStateActions.SET_CRUISE_LINES, cruiseLines: [] })
    onChange({ cruiseLines: [] })
    setShowAll(false)
    handlerClearSearchTerm()
  }, [handlerClearSearchTerm, onChange, searchDispatch])

  const onOpen = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_opened',
      action: 'opened',
      category: 'cruise_line_filter',
      type: 'interaction',
    })
    openDropdown()
  }, [openDropdown])

  const cruiseLineLabel = useMemo(() => {
    if (isOpen) return searchTerm

    const [firstName] = cruiseLineNames
    const more = cruiseLineNames.length > 1 ? `+${cruiseLineNames.length - 1}` : ''
    return firstName && `${firstName} ${more}`
  }, [cruiseLineNames, isOpen, searchTerm])

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputTriggerRef.current?.focus()
      openDropdown()
    },
  }), [openDropdown])

  return (
    <div data-testid="cruise-line-select-desktop">
      <SearchFormTextField
        onClick={onOpen}
        ref={inputTriggerRef}
        label="Cruise lines"
        placeholder={isOpen ? 'Search cruise lines' : 'All cruise lines'}
        value={cruiseLineLabel}
        displayValue={cruiseLineLabel}
        onChange={handlerSearchTerm}
        onClear={handlerClearSearchTerm}
      />

      <DropdownSheet
        size="fill-anchor"
        data-testid="cruise-line-select-dropdown"
        anchorRef={dropdownAnchorRef}
        triggerRef={inputTriggerRef}
        open={isOpen}
        onClose={closeDropdown}
        primaryActionProps={{
          'data-testid': 'cruise-filter-footer-apply',
          children: 'Apply',
          onClick: onApply,
        }}
        secondaryActionProps={{
          'data-testid': 'cruise-filter-footer-clear-all',
          kind: 'tertiary',
          children: 'Reset all',
          onClick: onClear,
        }}
        footerStart={<FormattedCruiseLineSearchSummary />}
      >
        <CruiseLinesSearchList
          showAll={showAll}
          onSetShowAll={setShowAll}
          searchTerm={searchTerm}
        />
      </DropdownSheet>
    </div>
  )
})

CruiseLineSelectDesktop.displayName = 'CruiseLineSelectDesktop'

export default CruiseLineSelectDesktop
